/**
 * PDF Generator — uses jsPDF for in-browser PDF creation
 * Fully calibrated for Innovation Hacks Offer Letter & Certificate Templates
 */
import { generateQRDataURL, getVerificationURL } from './qrGenerator';

/**
 * Generate PDF with Custom or Default Template
 */
export async function generateCertificateWithTemplate(candidate, templateUrl, settings, mode = 'internship') {
  const { jsPDF } = await import('jspdf');

  // 1. Load template image with robust fallback
  let img = null;
  if (templateUrl) {
    try {
      img = await new Promise((res) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => res(i);
        i.onerror = () => res(null);
        i.src = templateUrl;
      });
    } catch {
      img = null;
    }
  }

  const defaultFallback = mode === 'certificate' ? '/Certificate.jpeg' : '/template.png';

  // If provided template failed to load, try default fallback
  if (!img) {
    try {
      img = await new Promise((res) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => res(i);
        i.onerror = () => res(null);
        i.src = defaultFallback;
      });
    } catch {
      img = null;
    }
  }

  const isPortrait = img ? (img.width / img.height < 1.1) : (mode === 'internship');
  const W = isPortrait ? 210 : 297;
  const H = isPortrait
    ? 297
    : (img && img.width ? Math.round(297 * (img.height / img.width) * 10) / 10 : 237.7);

  const doc = new jsPDF({
    orientation: isPortrait ? 'portrait' : 'landscape',
    unit: 'mm',
    format: [W, H],
  });

  // 2. Draw template image or procedural high-end vector background
  if (img) {
    doc.addImage(img, 'JPEG', 0, 0, W, H, undefined, 'FAST');
  } else {
    // Elegant fallback background if no template image file
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 0, W, H, 'F');
    // Navy & Sky borders
    doc.setDrawColor(14, 165, 233);
    doc.setLineWidth(1.5);
    doc.rect(8, 8, W - 16, H - 16, 'S');
    doc.setDrawColor(15, 23, 42);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, W - 20, H - 20, 'S');

    // Header Logo & Org Name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text('INNOVATION HACKS', W / 2, 26, { align: 'center' });
    doc.setFontSize(8);
    doc.setTextColor(14, 165, 233);
    doc.text('BUILD • INNOVATE • IMPACT', W / 2, 31, { align: 'center' });
  }

  // 3. Generate QR Code with Verification URL
  // Use generatedId (e.g. IH-FS-2026-0001) so QR matches the printed intern ID
  const verifyId = candidate.generatedId || candidate.verificationId || 'IH-FS-2026-0001';
  const verifyUrl = getVerificationURL(verifyId);
  const qrDataUrl = await generateQRDataURL(verifyUrl, 300);

  // ════════════════════════════════════════════════════════════════════
  if (isPortrait) {
    // ── OFFER LETTER LAYOUT — Innovation Hacks Portrait Template ────────
    //
    //  RENDERING MODEL
    //  ───────────────
    //  The template image (template.png) pre-prints every static element:
    //    • Page background, borders, diagonal accents
    //    • Logo, tagline, contact info block (top-right)
    //    • "INTERNSHIP OFFER LETTER" title + decorative underline
    //    • Field labels: Name, Internship ID, Duration, Role
    //    • Colon separators aligned at SEPARATOR_X in the template
    //    • "Dear" word with its underline
    //    • Full letter body, "Best Regards", signature strip, footer
    //    • VERIFIED panel: outer border, inner badge, "Scan to Verify" label
    //
    //  This code overlays ONLY the per-candidate dynamic data:
    //    1. Candidate name value         → VALUE_X, ROW_NAME_Y
    //    2. Internship ID value          → VALUE_X, ROW_ID_Y
    //    3. Duration value               → VALUE_X, ROW_DURATION_Y
    //    4. Role value                   → VALUE_X, ROW_ROLE_Y
    //    5. Candidate name after "Dear"  → DEAR_NAME_X, DEAR_Y
    //    6. QR code image                → QR_X, QR_Y, QR_SIZE
    //
    // ════════════════════════════════════════════════════════════════════

    // ── LAYOUT GRID CONSTANTS (all in mm, A4 origin = top-left corner) ──
    //
    //  PIXEL-CALIBRATED from template.png (1055×1491 px = 5.0238 px/mm)
    //  Measurement script: scan_x.js / calibrate.js
    //
    //  Three-column grid for the information block:
    //
    //    Col 1  LABEL_X      — left edge of all field labels
    //    Col 2  SEPARATOR_X  — X of all ":" characters  (measured: ~42mm)
    //    Col 3  VALUE_X      — left edge of ALL value overlays
    //
    //  Template colon right-edges measured at:
    //    Name row         → 44.59 mm
    //    Internship ID row → 44.59 mm
    //    Duration row     → 44.39 mm
    //  → All colons end at exactly ~44.5 mm
    //  → VALUE_X = 44.5 + 3.5 mm gap = 48.0 mm (clean breathing room)
    //
    const LABEL_X = 14.93;  // Left edge of all labels (measured: 75px / 5.0238)
    const SEPARATOR_X = 42.0;   // X where ":" character starts  (reference only)
    const VALUE_X = 49.0;   // Left edge of ALL value text — tight gap matching Role row spacing

    //  ROW baselines — PIXEL-CALIBRATED to the actual template text rows:
    //    Name row         measured center: 107.9 mm
    //    Internship ID    measured center: 117.6 mm  (gap: 9.7 mm)
    //    Duration         measured center: 126.8 mm  (gap: 9.2 mm)
    //    NOTE: Template has NO pre-printed Role row. Role label+colon
    //    are rendered by this code in the blank space between Duration
    //    and Dear. Spacing matches the template rhythm.
    //    Dear             measured center: 141.7 mm
    //
    const ROW_NAME_Y = 107.9;               // "Name"          row — label baseline
    const ROW_ID_Y = 117.6;               // "Internship ID" row — label baseline
    const ROW_DURATION_Y = 126.8;               // "Duration"      row — label baseline

    //  Per-row colon-aligned Y positions.
    //  Name: -3.5 mm | ID: -4.5 mm (perfect) | Duration: -5.0 mm (perfect)
    const NAME_Y  = ROW_NAME_Y  - 3.3;   // 104.6 mm — Name colon baseline
    const ID_Y    = ROW_ID_Y    - 4.5;   // 113.1 mm — Internship ID colon baseline (perfect)
    const DUR_Y   = ROW_DURATION_Y - 5.0; // 121.8 mm — Duration colon baseline (perfect)

    //  DEAR section — candidate name on the same baseline as "Dear".
    //  "Dear" text measured at y=141.7 mm; name starts after "Dear ".
    //  DEAR_NAME_X measured: "Dear" word ends ~22.5mm, name starts at 27mm
    //  (includes a comfortable visual gap after the word).
    //
    const DEAR_NAME_X = 27.0;   // X start of name — right after "Dear "
    const DEAR_Y = 142.0;  // "Dear" row baseline — nudged down 0.3mm

    //  QR CODE — mathematically centered inside the VERIFIED box white area.
    //
    //  VERIFIED box inner white area (measured from template):
    //    X: 153 → 188 mm  (35 mm wide)
    //    Y: 100 → 135 mm  (below VERIFIED header, above Scan to Verify)
    //
    //  Target: 80% fill → QR_SIZE = 28 mm
    //  Center X = (153+188)/2 = 170.5  → QR_X = 170.5 - 14 = 156.5 ≈ 157
    //  Center Y = (100+135)/2 = 117.5  → QR_Y = 117.5 - 14 = 103.5 ≈ 103
    //
    const QR_X    = 157;   // left edge — horizontally centered in white area
    const QR_Y    = 101;   // top edge  — nudged up ~2mm (≈10px) from previous 103
    const QR_SIZE = 28;    // 80% of 35mm inner width — size unchanged

    // ── SHARED TYPOGRAPHY HELPERS ─────────────────────────────────────
    //
    //  applyValueStyle() — for all dynamic value text (Name, ID, Duration, Role)
    //  applyLabelStyle() — for the Role label+colon rendered by this code
    //  applyDearStyle()  — for the candidate name after "Dear"
    //
    //  NEVER set font/size/color inline — always go through these helpers.
    //
    const applyValueStyle = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(15, 23, 42);   // Dark navy — uniform across ALL value rows
    };

    const applyDearStyle = () => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.setTextColor(15, 23, 42);
    };

    // ── 1. NAME VALUE ─────────────────────────────────────────────────
    //  Template has blank value area — no pre-printed text to cover.
    //  Write directly at the label baseline. UPPERCASE for corporate style.
    //
    applyValueStyle();
    doc.text((candidate.name || '').toUpperCase(), VALUE_X, NAME_Y);

    // ── 2. INTERNSHIP ID VALUE ────────────────────────────────────────
    //  Template has blank value area — write directly at label baseline.
    //
    applyValueStyle();
    doc.text(candidate.generatedId || verifyId, VALUE_X, ID_Y);

    // ── 3. DURATION VALUE ─────────────────────────────────────────────
    //  Template has blank value area — write directly at label baseline.
    //  No white rect — avoids covering the pre-printed Role row below.
    //
    applyValueStyle();
    const durationText = candidate.duration
      || candidate.raw?.Duration
      || candidate.raw?.duration
      || '1 Month';
    doc.text(durationText, VALUE_X, DUR_Y);

    // ── 4. DEAR [CANDIDATE NAME] ──────────────────────────────────────
    //  Template pre-prints: "Dear" word at y=141.7mm.
    //  Code overlays ONLY the candidate name immediately after "Dear ".
    //  DEAR_NAME_X = 27.0mm (right after the "Dear" word).
    //  DEAR_Y      = 141.7mm (calibrated to the template's Dear baseline).
    //
    applyDearStyle();
    doc.text(candidate.name || '', DEAR_NAME_X, DEAR_Y);

    // ── 6. QR CODE ────────────────────────────────────────────────────
    //  Placed inside the template's pre-printed VERIFIED panel.
    //  Position and size must not change.
    //
    doc.addImage(qrDataUrl, 'PNG', QR_X, QR_Y, QR_SIZE, QR_SIZE);

  } else {
    // ── LANDSCAPE CERTIFICATE TEMPLATE CALIBRATION ──────────────────
    // Template: Certificate.jpeg (1402 × 1122 px, W=297mm, H=237.7mm)
    const cx = W / 2;

    // 1. Candidate Name (Centered right above the golden underline, lightly down)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(15, 23, 42);
    doc.text((candidate.name || '').toUpperCase(), cx, 111.5, { align: 'center' });

    // 2. Intern ID (Centered cleanly above the designated 'InternID :' underline)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    const internIdStr = candidate.generatedId || candidate.verificationId || verifyId;
    doc.text(internIdStr, 162.0, 175.5, { align: 'center' });

    // 3. QR Code (Calibrated to fit inside the golden square frame on the bottom right)
    const QR_BOX_X = 239.5;
    const QR_BOX_Y = 147.0;
    const QR_BOX_SIZE = 31.5;
    doc.addImage(qrDataUrl, 'PNG', QR_BOX_X, QR_BOX_Y, QR_BOX_SIZE, QR_BOX_SIZE);
  }

  return doc;
}

/**
 * Download single PDF using template with guaranteed .pdf extension
 */
export async function downloadCertificateWithTemplate(candidate, templateUrl, settings, mode) {
  const doc = await generateCertificateWithTemplate(candidate, templateUrl, settings, mode);
  const cleanName = (candidate.name || 'Candidate').replace(/[^a-zA-Z0-9_-]/g, '_');
  const cleanId = (candidate.generatedId || candidate.verificationId || 'INT').replace(/[^a-zA-Z0-9_-]/g, '_');
  const docType = mode === 'internship' ? 'Offer_Letter' : 'Certificate';
  const filename = `${cleanName}_${cleanId}_${docType}.pdf`;

  // Native jsPDF save triggers reliable download to Downloads folder
  doc.save(filename);
}

/**
 * Open single PDF directly in a new browser tab
 */
export async function openCertificatePDF(candidate, templateUrl, settings, mode) {
  const doc = await generateCertificateWithTemplate(candidate, templateUrl, settings, mode);
  const blob = doc.output('blob');
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
}

/**
 * Fallback Default Standard PDF
 */
export async function generateCertificatePDF(candidate, settings, mode = 'certificate') {
  const fallback = mode === 'certificate' ? '/Certificate.jpeg' : '/template.png';
  return generateCertificateWithTemplate(candidate, fallback, settings, mode);
}

export async function downloadCertificatePDF(candidate, settings, mode = 'certificate') {
  const fallback = mode === 'certificate' ? '/Certificate.jpeg' : '/template.png';
  return downloadCertificateWithTemplate(candidate, fallback, settings, mode);
}

export async function getCertificateBlob(candidate, settings, mode = 'certificate') {
  const fallback = mode === 'certificate' ? '/Certificate.jpeg' : '/template.png';
  const doc = await generateCertificateWithTemplate(candidate, fallback, settings, mode);
  return doc.output('blob');
}
